// src/components/sharedCollection/SharedCollectionPhotoGrid.tsx
import React, { useMemo } from 'react';
import { Box, Card, CardContent, CardMedia, Grid, SxProps, Theme, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
   PlayCircleOutlined as PlayCircleOutlinedIcon,
   ChatBubbleOutline as ChatBubbleOutlineIcon,
} from '@mui/icons-material';
import PhotoLikeButton from '../common/PhotoLikeButton';
import { SharedCollection, SharedPhoto } from '../../types/SharedCollection';
import {
   hasVideoImagePoster,
   isSharedVideoPhoto,
   videoPlaybackSrc,
} from '../../utils/sharedCollectionMedia';

/**
 * Layout ids must stay aligned with `LAYOUT_OPTIONS` / `LayoutType` in
 * `src/types/photoSharing.ts` and the share modal.
 * Covered: single-hero, grid-square, grid-instagram, story-horizontal,
 * masonry-pinterest, collage-puzzle, asymmetrical-modern, bordered-classic,
 * magazine-editorial, mosaic-creative, plus default for unknown/legacy values.
 */

export interface SharedCollectionPhotoGridProps {
   collection: SharedCollection;
   photos: SharedPhoto[];
   /** Used for like context and disabling when anonymous */
   currentUserId: string;
   /** When true, like buttons do not accept interactions */
   likeButtonsDisabled?: boolean;
   /** Show truncated description under each tile (e.g. social collection view) */
   showPhotoDescriptions?: boolean;
   onPhotoClick: (index: number) => void;
   onPhotoLikeChange: (originalPhotoId: string, isLiked: boolean, likeCount: number) => void;
   /** Counts keyed by `originalPhotoId`; shown next to the comment control when `onPhotoCommentClick` is set. */
   commentCountByPhotoId?: Record<string, number>;
   /** Invoked from the comment control only; use `stopPropagation` so the tile still opens the lightbox on media click. */
   onPhotoCommentClick?: (index: number) => void;
}

const VideoPlayOverlay: React.FC = () => (
   <PlayCircleOutlinedIcon
      sx={{
         position: 'absolute',
         left: '50%',
         top: '50%',
         transform: 'translate(-50%, -50%)',
         fontSize: 40,
         color: 'common.white',
         filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.85))',
         pointerEvents: 'none',
      }}
   />
);

type MediaPreviewProps = {
   photo: SharedPhoto;
   onClick: () => void;
   /** Used for image tiles and as fallback when not video */
   imageSrc: string;
   sx?: SxProps<Theme>;
};

/**
 * Grid / layout preview: image, or video poster / muted first-frame preview with play icon.
 */
const CollectionMediaPreview: React.FC<MediaPreviewProps> = ({ photo, onClick, imageSrc, sx }) => {
   if (!isSharedVideoPhoto(photo)) {
      return (
         <CardMedia
            component='img'
            image={imageSrc}
            alt={photo.title || ''}
            onClick={onClick}
            sx={sx}
         />
      );
   }

   if (hasVideoImagePoster(photo)) {
      return (
         <Box onClick={onClick} sx={{ position: 'relative', cursor: 'pointer', ...sx }}>
            <Box
               component='img'
               src={photo.thumbnailUrl}
               alt={photo.title || ''}
               sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
               }}
            />
            <VideoPlayOverlay />
         </Box>
      );
   }

   const raw = videoPlaybackSrc(photo);
   const src = raw.includes('#') ? raw : `${raw}#t=0.001`;

   return (
      <Box
         onClick={onClick}
         sx={{
            position: 'relative',
            cursor: 'pointer',
            overflow: 'hidden',
            bgcolor: 'common.black',
            ...sx,
         }}
      >
         <Box
            component='video'
            src={src}
            muted
            playsInline
            preload='metadata'
            sx={{
               width: '100%',
               height: '100%',
               objectFit: 'cover',
               display: 'block',
               pointerEvents: 'none',
            }}
         />
         <VideoPlayOverlay />
      </Box>
   );
};

const SharedCollectionPhotoGrid: React.FC<SharedCollectionPhotoGridProps> = ({
   collection,
   photos,
   currentUserId,
   likeButtonsDisabled = false,
   showPhotoDescriptions = false,
   onPhotoClick,
   onPhotoLikeChange,
   commentCountByPhotoId,
   onPhotoCommentClick,
}) => {
   const theme = useTheme();
   const photoCardSx = useMemo(
      () => ({
         borderRadius: 2,
         overflow: 'hidden' as const,
         border: 'none',
         bgcolor: 'transparent',
         boxShadow: 2,
         transition: 'all 0.3s ease',
      }),
      [],
   );

   const tileFooterSx = useMemo(
      () => ({
         p: 1,
         display: 'flex',
         flexDirection: 'column' as const,
         alignItems: 'flex-start' as const,
         bgcolor: alpha(theme.palette.common.black, 0.28),
         borderTop: 'none',
      }),
      [theme],
   );

   const storyTrackSx = useMemo(
      () => ({
         overflowX: 'auto' as const,
         pb: 2,
         '&::-webkit-scrollbar': { height: 8 },
         '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.primary.main, 0.35),
            borderRadius: 4,
         },
         '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(theme.palette.common.white, 0.04),
            borderRadius: 4,
         },
      }),
      [theme],
   );

   if (photos.length === 0) return null;

   const layout = collection.layout || 'grid-square';
   const collectionId = collection.id;
   const likeCtx = { type: 'collection' as const, id: collectionId };

   const renderCommentControl = (photo: SharedPhoto, index: number) => {
      if (!onPhotoCommentClick) return null;
      const n = commentCountByPhotoId?.[photo.originalPhotoId] ?? 0;
      return (
         <Box
            component='button'
            type='button'
            onClick={(e) => {
               e.preventDefault();
               e.stopPropagation();
               onPhotoCommentClick(index);
            }}
            sx={{
               display: 'inline-flex',
               alignItems: 'center',
               gap: 0.35,
               ml: 1,
               py: 0.35,
               px: 0.5,
               border: 'none',
               borderRadius: 1,
               background: alpha(theme.palette.common.white, 0.06),
               cursor: 'pointer',
               color: 'text.secondary',
               transition: 'color 0.15s, background-color 0.15s',
               '&:hover': {
                  color: 'text.primary',
                  bgcolor: alpha(theme.palette.common.white, 0.1),
               },
            }}
            aria-label={`Comment on photo, ${n} comments`}
         >
            <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
            <Typography variant='caption' component='span'>
               {n}
            </Typography>
         </Box>
      );
   };

   const renderLike = (photo: SharedPhoto) => (
      <PhotoLikeButton
         photoId={photo.originalPhotoId}
         userId={currentUserId}
         context={likeCtx}
         initialLikeCount={photo.likeCount || 0}
         initialIsLiked={photo.isLiked || false}
         size='small'
         variant='compact'
         showCount={true}
         disabled={likeButtonsDisabled}
         onLikeChange={(isLiked, likeCount) => {
            onPhotoLikeChange(photo.originalPhotoId, isLiked, likeCount);
         }}
      />
   );

   const renderLikeBar = (photo: SharedPhoto, index: number) => (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
         {renderLike(photo)}
         {renderCommentControl(photo, index)}
      </Box>
   );

   const renderDescription = (photo: SharedPhoto) =>
      showPhotoDescriptions && photo.description ? (
         <Typography
            variant='caption'
            color='textSecondary'
            sx={{
               display: '-webkit-box',
               WebkitLineClamp: 2,
               WebkitBoxOrient: 'vertical',
               overflow: 'hidden',
               mt: 0.5,
            }}
         >
            {photo.description}
         </Typography>
      ) : null;

   switch (layout) {
      case 'single-hero': {
         const hero = photos[0];
         const imgSrc = hero.url || hero.thumbnailUrl;
         return (
            <Box textAlign='center'>
               <Card elevation={0}
                  sx={{
                     ...photoCardSx,
                     maxWidth: 800,
                     margin: '0 auto',
                     cursor: 'pointer',
                  }}
               >
                  <CollectionMediaPreview
                     photo={hero}
                     onClick={() => onPhotoClick(0)}
                     imageSrc={imgSrc}
                     sx={{ height: 300, objectFit: 'cover' }}
                  />
                  <CardContent sx={{ ...tileFooterSx, flexDirection: 'row', justifyContent: 'flex-start' }}>
                     {renderLikeBar(hero, 0)}
                  </CardContent>
               </Card>
            </Box>
         );
      }

      case 'grid-square':
      case 'grid-instagram':
         return (
            <Grid container spacing={2}>
               {photos.map((photo, index) => (
                  <Grid item xs={6} sm={4} md={3} key={photo.id}>
                     <Card elevation={0} sx={{ ...photoCardSx, cursor: 'pointer' }}>
                        <CollectionMediaPreview
                           photo={photo}
                           onClick={() => onPhotoClick(index)}
                           imageSrc={photo.thumbnailUrl || photo.url}
                           sx={{
                              height: 200,
                              objectFit: 'cover',
                           }}
                        />
                        <Box sx={{ ...tileFooterSx }}>
                           {renderLikeBar(photo, index)}
                           {renderDescription(photo)}
                        </Box>
                     </Card>
                  </Grid>
               ))}
            </Grid>
         );

      case 'story-horizontal':
         return (
            <Box display='flex' gap={2} sx={storyTrackSx}>
               {photos.map((photo, index) => (
                  <Card elevation={0}
                     key={photo.id}
                     sx={{
                        ...photoCardSx,
                        minWidth: 300,
                        cursor: 'pointer',
                        flexShrink: 0,
                     }}
                  >
                     <CollectionMediaPreview
                        photo={photo}
                        onClick={() => onPhotoClick(index)}
                        imageSrc={photo.url || photo.thumbnailUrl}
                        sx={{ height: 250, objectFit: 'cover' }}
                     />
                     <CardContent sx={{ ...tileFooterSx }}>
                        {renderLikeBar(photo, index)}
                        {renderDescription(photo)}
                     </CardContent>
                  </Card>
               ))}
            </Box>
         );

      case 'masonry-pinterest':
         return (
            <Box
               sx={{
                  columnCount: { xs: 1, sm: 2, md: 3 },
                  columnGap: 2,
               }}
            >
               {photos.map((photo, index) => (
                  <Card elevation={0}
                     key={photo.id}
                     sx={{
                        ...photoCardSx,
                        cursor: 'pointer',
                        mb: 1,
                        breakInside: 'avoid',
                        pageBreakInside: 'avoid',
                     }}
                  >
                     <CollectionMediaPreview
                        photo={photo}
                        onClick={() => onPhotoClick(index)}
                        imageSrc={photo.url || photo.thumbnailUrl}
                        sx={{
                           width: '100%',
                           height: 'auto',
                           maxHeight: 360,
                           minHeight: isSharedVideoPhoto(photo) && !hasVideoImagePoster(photo) ? 220 : undefined,
                           objectFit: 'cover',
                           display: 'block',
                        }}
                     />
                     <CardContent sx={{ ...tileFooterSx }}>
                        {renderLikeBar(photo, index)}
                        {renderDescription(photo)}
                     </CardContent>
                  </Card>
               ))}
            </Box>
         );

      case 'collage-puzzle':
      case 'asymmetrical-modern':
      case 'bordered-classic':
      case 'magazine-editorial':
      case 'mosaic-creative':
      default:
         return (
            <Grid container spacing={2}>
               {photos.map((photo, index) => (
                  <Grid item xs={12} sm={6} md={4} key={photo.id}>
                     <Card elevation={0} sx={{ ...photoCardSx, cursor: 'pointer' }}>
                        <CollectionMediaPreview
                           photo={photo}
                           onClick={() => onPhotoClick(index)}
                           imageSrc={photo.url || photo.thumbnailUrl}
                           sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 300,
                              minHeight: isSharedVideoPhoto(photo) && !hasVideoImagePoster(photo) ? 200 : undefined,
                              objectFit: 'cover',
                           }}
                        />
                        <CardContent sx={{ ...tileFooterSx }}>
                           {renderLikeBar(photo, index)}
                           {renderDescription(photo)}
                        </CardContent>
                     </Card>
                  </Grid>
               ))}
            </Grid>
         );
   }
};

export default SharedCollectionPhotoGrid;
